import type { CommunicationRecord } from "@entities";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { Button, Popover, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import {
  formatCommunicationRecordDate,
  getLatestCommunicationRecord,
} from "@shared/helpers/communication-record";
import { useState, type MouseEvent } from "react";
import { CommunicationRecordsDialog } from "./CommunicationRecordsDialog";

type BaseProps = {
  records: CommunicationRecord[] | undefined;
};

type WithDialogProps = BaseProps & {
  associationId?: string;
  summerCampaignLeadId?: string;
  partnerOrganizationId?: string;
  volunteerOrganizationId?: string;
  title: string;
  onSave?: (records?: CommunicationRecord[]) => void;
  onViewAll?: never;
};

type WithCallbackProps = BaseProps & {
  onViewAll: () => void;
  associationId?: never;
  summerCampaignLeadId?: never;
  partnerOrganizationId?: never;
  volunteerOrganizationId?: never;
  title?: never;
  onSave?: never;
};

type Props = WithDialogProps | WithCallbackProps;

function stopRowClick(event: MouseEvent, action: () => void) {
  event.stopPropagation();
  action();
}

export function CommunicationRecordsTableCell(props: Props) {
  const { records } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const lastRecord = getLatestCommunicationRecord(records);

  const openFullView = () => {
    if (props.onViewAll) {
      props.onViewAll();
      return;
    }
    setDialogOpen(true);
  };

  const dialog =
    props.title &&
    (props.associationId ||
      props.summerCampaignLeadId ||
      props.partnerOrganizationId ||
      props.volunteerOrganizationId) ? (
      <CommunicationRecordsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={props.title}
        associationId={props.associationId}
        summerCampaignLeadId={props.summerCampaignLeadId}
        partnerOrganizationId={props.partnerOrganizationId}
        volunteerOrganizationId={props.volunteerOrganizationId}
        type={
          props.volunteerOrganizationId
            ? "volunteer-organization"
            : props.partnerOrganizationId
              ? "partner-organization"
              : props.summerCampaignLeadId
                ? "lead"
                : "volunteer"
        }
        onSave={props.onSave}
      />
    ) : null;

  if (!lastRecord) {
    return (
      <>
        <Button
          size="small"
          variant="text"
          startIcon={<ForumOutlinedIcon fontSize="small" />}
          onClick={(event) => stopRowClick(event, openFullView)}
          sx={{ whiteSpace: "nowrap" }}
        >
          הוספת תיעוד
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
        <Button
          size="small"
          variant="text"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
          sx={{ minWidth: 0, px: 0.5, whiteSpace: "nowrap" }}
        >
          {formatCommunicationRecordDate(lastRecord)}
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={(event) => stopRowClick(event, openFullView)}
          sx={{ minWidth: 0, px: 0.5, whiteSpace: "nowrap" }}
        >
          הצג הכל
        </Button>
      </Stack>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { maxWidth: 420, p: 2 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          {lastRecord.title || "תיעוד תקשורת"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {format(new Date(lastRecord.createdAt), "dd/MM/yyyy HH:mm")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, maxHeight: 260, overflow: "auto" }}
        >
          {lastRecord.description || "אין תיאור"}
        </Typography>
      </Popover>
      {dialog}
    </>
  );
}
